import { FC, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  getSurveysCampaignsCall,
  useSurvey,
} from '@/surveys/state';
import {
  getAdminContentsLikesByContentTypeCall,
  ContentLike,
} from '../../../../contentsLikes/state';
import { CardItem, COLORS_PLAYGROUND_CARDS } from '../../../state';
import { CardContact } from './CardContact';
import { CardsPlayground } from './CardsPlayground';
import { IntroPlayground } from './IntroPlayground';

export const LandingPlayground: FC = () => {
  const tShared = useTranslations();

  const {
    execute: executeGetSurveysCampaigns,
    loading: loadingGetSurveysCampaigns,
    error: errorGetSurveysCampaigns,
    success: successGetSurveysCampaigns,
  } = getSurveysCampaignsCall.hook();

  const {
    execute: executeGetSurveyCampaignByIdResponsesResultsStats,
    loading: loadingGetSurveyCampaignByIdResponsesResultsStats,
    error: errorGetSurveyCampaignByIdResponsesResultsStats,
  } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { response: responseGetSurveyCampaignByIdResponsesResultsStats } =
    getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { execute: executeGetAdminContentsLikesByContentType } =
    getAdminContentsLikesByContentTypeCall.hook();

  const { softSkillGetTemplateId, softSkillGetListMetrics } = useSurvey();

  const data = useMemo(() => {
    const elaboratedData: CardItem[] = [];
    if (
      responseGetSurveyCampaignByIdResponsesResultsStats &&
      responseGetSurveyCampaignByIdResponsesResultsStats?.organizationStats &&
      Array.isArray(
        responseGetSurveyCampaignByIdResponsesResultsStats.organizationStats?.metricsScore,
      ) &&
      responseGetSurveyCampaignByIdResponsesResultsStats.organizationStats.metricsScore.length > 0
    ) {
      const { totalCompletedSurveys } =
        responseGetSurveyCampaignByIdResponsesResultsStats.organizationStats;

      responseGetSurveyCampaignByIdResponsesResultsStats.organizationStats?.metricsScore.forEach(
        item => {
          const { beginningPercentage } = item;
          elaboratedData.push({
            title: item.metric?.name || '',
            peopleWithInitialLevel:
              totalCompletedSurveys &&
              beginningPercentage &&
              Number(totalCompletedSurveys * (beginningPercentage / 100)).toFixed(0),
          } as CardItem);
        },
      );
    }

    if (
      responseGetSurveyCampaignByIdResponsesResultsStats &&
      Array.isArray(responseGetSurveyCampaignByIdResponsesResultsStats.departmentsStats) &&
      responseGetSurveyCampaignByIdResponsesResultsStats.departmentsStats.length > 0
    ) {
      const { departmentsStats } = responseGetSurveyCampaignByIdResponsesResultsStats;

      const uniqueDepartments = [...new Set(departmentsStats.map(d => d.departmentId))];

      uniqueDepartments.forEach(departmentId => {
        const departmentData = departmentsStats.find(d => d.departmentId === departmentId);

        if (
          departmentData &&
          Array.isArray(departmentData.metricsScore) &&
          departmentData.metricsScore.length > 0
        ) {
          departmentData.metricsScore.forEach(item => {
            const { beginningPercentage } = item;

            if (beginningPercentage && beginningPercentage > 0) {
              // eslint-disable-next-line max-nested-callbacks
              const existing = elaboratedData.find(e => e.title === item.metric?.name);
              if (existing) {
                existing.totalDepartments = (existing.totalDepartments || 0) + 1;
              }
            }
          });
        }
      });
    }

    return elaboratedData;
  }, [responseGetSurveyCampaignByIdResponsesResultsStats]);

  const metricsMap = useMemo(() => {
    const metricsScore =
      responseGetSurveyCampaignByIdResponsesResultsStats?.organizationStats?.metricsScore || [];

    const metricScoreMap = metricsScore.reduce<Record<string, number>>((acc, item) => {
      if (item.metric?.name && typeof item.score === 'number') {
        acc[item.metric.name] = item.score;
      }
      return acc;
    }, {});

    return softSkillGetListMetrics
      .map((metric, index) => {
        const { color1, color2 } = COLORS_PLAYGROUND_CARDS[index] || {
          color1: 'rgba(1, 253, 236, 1)',
          color2: 'rgba(27, 33, 41, 1)',
        };
        const currentData = data.find(item => item.title === metric.name);

        const metricName = metric.name ?? '';
        return {
          title: metricName,
          color1,
          color2,
          shortDescription: metric.shortDescription || '',
          longDescription: metric.longDescription || '',
          totalInterestedPeople: 0,
          peopleWithInitialLevel: currentData?.peopleWithInitialLevel || 0,
          totalDepartments: currentData?.totalDepartments || 0,
          score: metricScoreMap[metricName] ?? Number.MAX_SAFE_INTEGER,
        };
      })
      .sort((a, b) => a.score - b.score)
      .map(({ score: _score, ...rest }) => rest);
  }, [
    data,
    softSkillGetListMetrics,
    responseGetSurveyCampaignByIdResponsesResultsStats?.organizationStats?.metricsScore,
  ]);

  useEffect(() => {
    if (softSkillGetTemplateId) {
      executeGetSurveysCampaigns({
        successSaga: ({ response }) => {
          if (response.length > 0) {
            const latestCampaign = response
              .filter(
                (campaign: { surveyTemplateId: string }) =>
                  campaign.surveyTemplateId === softSkillGetTemplateId,
              )
              .slice(-1)[0];
            if (latestCampaign !== undefined) {
              executeGetSurveyCampaignByIdResponsesResultsStats({
                campaignId: latestCampaign.id,
              });
            }
          }
        },
      });
      executeGetAdminContentsLikesByContentType({
        contentType: ContentLike.AI_PLAYGROUND_CARD,
      });
    }
  }, [
    executeGetAdminContentsLikesByContentType,
    executeGetSurveyCampaignByIdResponsesResultsStats,
    executeGetSurveysCampaigns,
    softSkillGetTemplateId,
  ]);

  if (loadingGetSurveysCampaigns || loadingGetSurveyCampaignByIdResponsesResultsStats) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetSurveysCampaigns || errorGetSurveyCampaignByIdResponsesResultsStats) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mb: 's' }} iconName='info' type='error' label={message} />;
  }

  if (successGetSurveysCampaigns && !loadingGetSurveysCampaigns && !errorGetSurveysCampaigns) {
    return (
      <Box mb='xxl'>
        <IntroPlayground />
        <CardsPlayground
          metricsMap={metricsMap}
          elementToView={{
            desktopLarge: 4.5,
            desktop: 3.5,
            tablet: 2.5,
            mobile: 1,
          }}
        />
        <CardContact metricsMap={metricsMap} />
      </Box>
    );
  }

  return null;
};
